import { Component } from '@theme/component';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';
import { isClickedOutside } from '@theme/utilities';

/** @type {string} */
export const SearchModalOpenEventName = 'search-modal:open';

/**
 * Search modal UX: dialog open/close, keyboard shortcuts, input reset, and empty state.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchModalComponent extends Component {
  #controller = new AbortController();

  /** @returns {DialogComponent | null} */
  get dialogComponent() {
    const dialog = this.closest('dialog-component');
    return dialog instanceof DialogComponent ? dialog : null;
  }

  /** @returns {HTMLInputElement | null} */
  #activeSearchInput() {
    const aaInput = this.querySelector('#algolia-autocomplete-input .aa-Input');
    if (aaInput instanceof HTMLInputElement) return aaInput;

    if (this.refs.searchInput instanceof HTMLInputElement) return this.refs.searchInput;

    return null;
  }

  /**
   * @param {HTMLInputElement} input
   */
  #dispatchInputEvent(input) {
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialogComponent } = this;
    const { signal } = this.#controller;

    this.#syncQueryState();

    this.addEventListener(
      'input',
      (event) => {
        if (!(event.target instanceof HTMLInputElement)) return;
        if (!event.target.matches('.search-input, .aa-Input')) return;
        this.#handleInput();
      },
      { signal }
    );

    if (dialogComponent) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialogComponent.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialogComponent.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      dialogComponent.addEventListener('click', this.#handleClickOutside, { signal, capture: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleInput = () => {
    this.#syncQueryState();
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      event.preventDefault();
      this.dialogComponent?.toggleDialog();
    }
  };

  #handleDialogClose = () => {
    this.#clearSearch();
  };

  #handleDialogOpen = () => {
    requestAnimationFrame(() => {
      this.#activeSearchInput()?.focus();
      this.#syncQueryState();
      document.dispatchEvent(new CustomEvent(SearchModalOpenEventName));
    });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    const { dialogComponent } = this;
    if (!dialogComponent?.refs.dialog?.open) return;

    if (isClickedOutside(event, this)) {
      event.stopImmediatePropagation();
      dialogComponent.closeDialog();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  onSearchKeyDown = (event) => {
    const input = this.#activeSearchInput();
    if (!input) return;

    if (event.key === 'Escape' && input.value.trim().length > 0) {
      event.preventDefault();
      this.#clearSearch(true);
    }
  };

  #clearSearch(keepFocus = false) {
    const input = this.#activeSearchInput();
    if (!input) return;

    input.value = '';
    this.#syncQueryState();
    this.#dispatchInputEvent(input);

    if (keepFocus) {
      input.focus();
    }
  }

  #syncQueryState() {
    const input = this.#activeSearchInput();
    const hasQuery = Boolean(input?.value.trim().length);
    this.classList.toggle('search-modal--has-query', hasQuery);
  }
}

if (!customElements.get('search-modal-component')) {
  customElements.define('search-modal-component', SearchModalComponent);
}
